import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gift, Zap, Trophy, MapPin, Gem, Bell, Dice5, Flame } from "lucide-react";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";
import NeedsHUD from "@/components/NeedsHUD";
import Avatar from "@/components/Avatar";
import WalkingScene from "@/components/WalkingScene";

export default function Home() {
  const { character, setCharacter } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [actions, setActions] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a, n] = await Promise.all([
        api.get("/game/state"),
        api.get("/actions"),
        api.get("/social/notifications"),
      ]);
      setState(s.data);
      setCharacter(s.data.character);
      setActions(a.data.actions);
      setUnread(n.data.unread);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [setCharacter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [load]);

  const c = state?.character || character;

  const [chaosLeft, setChaosLeft] = useState(0);
  useEffect(() => {
    const tick = () => {
      const ready = c?.chaos_ready_at ? new Date(c.chaos_ready_at).getTime() : 0;
      setChaosLeft(Math.max(0, Math.ceil((ready - Date.now()) / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [c?.chaos_ready_at]);

  const doAction = async (id, title) => {
    try {
      const { data } = await api.post("/actions/do", { action_id: id });
      setCharacter(data.character);
      setState((s) => ({ ...s, character: data.character }));
      toast.success(title + " ✓");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const claimDaily = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/daily/claim");
      setCharacter(data.character);
      setState((s) => ({ ...s, character: data.character }));
      toast.success(`Daily reward: ${naira(data.reward)}${data.gems ? ` + ${data.gems}💎` : ""} · Streak ${data.streak}🔥`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const rollChaos = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/chaos/roll");
      setCharacter(data.character);
      setState((s) => ({ ...s, character: data.character }));
      const ev = data.event;
      const msg = `${ev.msg}${ev.wallet_delta ? ` (${ev.wallet_delta > 0 ? "+" : ""}${naira(ev.wallet_delta)})` : ""}`;
      if (ev.type === "good") toast.success(ev.title, { description: msg });
      else toast.error(ev.title, { description: msg });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!c) return null;

  const rentOverdue = state?.rent?.overdue;

  return (
    <div className="px-5 pt-6" data-testid="home-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar src={c.avatar} size={48} />
          <div>
            <div className="font-display text-xl leading-none">{c.name}</div>
            <div className="label-xs text-slate-500 mt-1 flex items-center gap-1">
              <MapPin size={10} /> {c.district_name}, {c.city} · Lv{c.level}
            </div>
          </div>
        </div>
        <button data-testid="notif-btn" onClick={() => nav("/social")} className="relative p-2">
          <Bell size={22} className="text-slate-500" />
          {unread > 0 && (
            <span className="absolute top-0 right-0 bg-[#FF0055] text-white text-[0.6rem] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      </div>

      {/* Walking scene */}
      <div className="mt-4">
        <WalkingScene character={c} />
      </div>

      {/* Wallet row */}
      <div className="mt-5 grid grid-cols-3 gap-2">
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="wallet-card">
          <div className="label-xs text-slate-500">Wallet</div>
          <div className="text-[#16A34A] font-display text-base mt-1 neon-text truncate">{naira(c.wallet)}</div>
        </div>
        <button onClick={() => nav("/leaderboard")} className="bg-white border border-slate-200 rounded-xl p-3 text-left hover:border-[#FF0055] transition-colors" data-testid="clout-card">
          <div className="label-xs text-slate-500">Clout</div>
          <div className="text-[#FF0055] font-display text-base mt-1 neon-text">{c.clout}</div>
        </button>
        <button onClick={() => nav("/store")} className="bg-white border border-slate-200 rounded-xl p-3 text-left hover:border-[#FFD700] transition-colors" data-testid="gems-card">
          <div className="label-xs text-slate-500">Gems</div>
          <div className="text-[#FFD700] font-display text-base mt-1 flex items-center gap-1"><Gem size={14} /> {c.gems}</div>
        </button>
      </div>

      {/* Rent warning */}
      {rentOverdue && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="mt-4 bg-[#FF0055]/15 border border-[#FF0055]/40 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-[#FF9ebc]">⚠️ Rent don pass due! Landlord dey vex.</span>
          <button data-testid="pay-rent-home-btn" onClick={() => nav("/map")} className="label-xs bg-[#FF0055] text-white px-3 py-1.5 rounded-full">Pay now</button>
        </motion.div>
      )}

      {/* Needs */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <span className="label-xs text-slate-500">Your Needs</span>
          <span className="label-xs text-slate-400">freezes offline</span>
        </div>
        <NeedsHUD needs={c.needs} />
      </div>

      {/* Action buttons: daily + chaos */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button data-testid="daily-claim-btn" onClick={claimDaily} disabled={busy}
          className="bg-[#FFD700] text-slate-900 font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-[#ffe34d] transition-colors disabled:opacity-50">
          <Gift size={18} /> Daily Reward
        </button>
        <button data-testid="chaos-roll-btn" onClick={rollChaos} disabled={busy || chaosLeft > 0}
          className="bg-white border border-[#00E5FF]/40 text-[#00E5FF] font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-[#00E5FF]/10 transition-colors disabled:opacity-50">
          <Dice5 size={18} /> {chaosLeft > 0 ? `Street Roll (${chaosLeft}s)` : "Street Roll"}
        </button>
      </div>

      {/* Quick life actions */}
      <div className="mt-6">
        <span className="label-xs text-slate-500">Handle Your Life</span>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {actions.map((a) => {
            const Icon = Icons[a.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Zap;
            return (
              <button
                key={a.id}
                data-testid={`action-${a.id}`}
                onClick={() => doAction(a.id, a.title)}
                className="bg-white border border-slate-200 rounded-xl p-3 text-left hover:border-[#16A34A] hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center justify-between">
                  <Icon size={18} className="text-[#16A34A]" />
                  <span className="text-xs text-slate-500">{a.cost ? naira(a.cost) : "Free"}</span>
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">{a.title}</div>
                <div className="text-[0.7rem] text-slate-500 mt-0.5 leading-tight">{a.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick nav tiles */}
      <div className="mt-6 grid grid-cols-3 gap-2 mb-4">
        <button onClick={() => nav("/jobs")} data-testid="quick-jobs" className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-[#16A34A] transition-colors">
          <Flame size={20} className="text-[#16A34A]" /><span className="label-xs text-slate-500">Hustle</span>
        </button>
        <button onClick={() => nav("/map")} data-testid="quick-map" className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-[#00E5FF] transition-colors">
          <MapPin size={20} className="text-[#00E5FF]" /><span className="label-xs text-slate-500">Housing</span>
        </button>
        <button onClick={() => nav("/leaderboard")} data-testid="quick-board" className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-[#FFD700] transition-colors">
          <Trophy size={20} className="text-[#FFD700]" /><span className="label-xs text-slate-500">Ranks</span>
        </button>
      </div>
    </div>
  );
}
